import Layout from "../components/Global/Layout/Layout"
import SEO from "../components/Global/SEO/seo"
import HeroSection from "../components/CountryReportsFocus/HeroSection/HeroSection"
import Population from "../components/CountryReportsFocus/Population/Population"
import Ecommerce from "../components/CountryReportsFocus/Ecommerce/Ecommerce"
import PaymentPreferences from "../components/CountryReportsFocus/PaymentPreferences/PaymentPreferences"
import Economy from "../components/CountryReportsFocus/Economy/Economy"
import InternetPhoneTrends from "../components/CountryReportsFocus/InternetPhoneTrends/InternetPhoneTrends"
import Banking from "../components/CountryReportsFocus/Banking/Banking"
import AlternatingRows from "../components/CountryReportsFocus/AlternatingRows/AlternatingRows"
import { CountryReportsFocusStyles } from "../components/CountryReportsFocus/CountryReportsFocus.styles.js"
import React from "react"
import { graphql } from "gatsby"

const Country = ({ data }) => {
  const post = data && data.statamic && data.statamic.entry
  if (!post) return null

  const hideMainContent = post.only_show_country_report_rows

  const flagCode = Array.isArray(post.country_flag)
    ? post.country_flag[0]
    : post.country_flag

  const populationData = post.population_data || []
  const ecommerceData = post.ecommerce_data_points || []
  const paymentPrefs = post.payment_preferences || []
  const economyChart = post.economy_chart || []
  const internetPhoneChart = post.internet_phone_trends_chart || []
  const bankingStats = post.banking_statistics || []
  const customRows = post.custom_rows || []

  const heroSection =
    post.title || post.description || flagCode ? (
      <HeroSection
        title={post.title}
        description={post.description}
        flagCode={flagCode}
      />
    ) : null

  const population =
    !hideMainContent &&
    (post.population_description || populationData.length > 0) ? (
      <Population
        description={post.population_description}
        populationDataYears={populationData.map((p) => p && p.year)}
        populationDataAmount={populationData.map((p) => p && p.amount)}
        populationIncrement={populationData.map((p) => p && p.increment)}
      />
    ) : null

  const ecommerce =
    !hideMainContent &&
    (post.ecommerce_description || ecommerceData.length > 0) ? (
      <Ecommerce
        description={post.ecommerce_description}
        dataTitles={ecommerceData.map((d) => d && d.title)}
        dataValues={ecommerceData.map((d) => d && d.value)}
      />
    ) : null

  const paymentPrefrences =
    !hideMainContent &&
    (post.payment_preferences_description || paymentPrefs.length > 0) ? (
      <PaymentPreferences
        description={post.payment_preferences_description}
        keys={paymentPrefs.map((p) => p && p.key)}
        logos={paymentPrefs.map((p) => p && p.logo)}
        values={paymentPrefs.map((p) => p && p.value)}
      />
    ) : null

  const economy =
    !hideMainContent &&
    (post.economy_description || economyChart.length > 0) ? (
      <Economy
        description={post.economy_description}
        keys={economyChart.map((e) => e && e.key)}
        values={economyChart.map((e) => e && e.value)}
        increment={economyChart.map((e) => e && e.increment)}
      />
    ) : null

  const internetPhoneTrends =
    !hideMainContent &&
    (post.internet_phone_trends_description ||
      internetPhoneChart.length > 0) ? (
      <InternetPhoneTrends
        description={post.internet_phone_trends_description}
        labels={internetPhoneChart.map((c) => c && c.label)}
        values={internetPhoneChart.map((c) => c && c.value)}
      />
    ) : null

  const banking =
    !hideMainContent &&
    (post.banking_description || bankingStats.length > 0) ? (
      <Banking
        description={post.banking_description}
        dataTitles={bankingStats.map((b) => b && b.title)}
        dataValues={bankingStats.map((b) => b && b.value)}
      />
    ) : null

  const customRowElements = customRows
    .filter((row) => row && row.description)
    .map((row, i) => (
      <AlternatingRows
        key={i}
        rowData={row}
        rowAlternation={i % 2 === 0 ? "even" : "odd"}
      />
    ))

  const metaTitle = post.meta_title || `Payment Gateway ${post.title}`
  const metaDescription =
    post.meta_description ||
    `APEXX specialises in guiding enterprise merchants into new markets globally. Find out more about the payments landscape in ${post.title} by using our educational report.`

  return (
    <Layout headerTransparent whiteToggler logoDark={false}>
      <CountryReportsFocusStyles>
        <SEO
          page="country-report"
          title={metaTitle}
          description={metaDescription}
        />
        {heroSection}
        {population}
        {ecommerce}
        {paymentPrefrences}
        {economy}
        {internetPhoneTrends}
        {banking}
        {customRowElements}
        <section className="px-4">
          <div className="pt-3 border-top d-flex flex-column align-items-center container"></div>
        </section>
      </CountryReportsFocusStyles>
    </Layout>
  )
}

export default Country

export const query = graphql`
  query GetCountryReport($slug: String!) {
    statamic {
      entry(collection: "country_reports", slug: $slug) {
        id
        title
        slug
        ... on Statamic_Entry_CountryReports_CountryReport {
          meta_title
          meta_description
          country_name
          country_flag
          description
          only_show_country_report_rows
          population_description
          population_data {
            year
            amount
            increment {
              value
              label
            }
          }
          ecommerce_description
          ecommerce_data_points {
            title
            value
          }
          payment_preferences_description
          payment_preferences {
            key
            value
            logo
          }
          economy_description
          economy_chart {
            key
            value
            increment {
              value
              label
            }
          }
          internet_phone_trends_description
          internet_phone_trends_chart {
            label
            value
          }
          banking_description
          banking_statistics {
            title
            value
          }
          custom_rows {
            ... on Statamic_Set_CustomRows_CountryReportRow {
              type
              title
              row_background_colour {
                value
              }
              description
              image {
                url
                permalink
                ... on Statamic_Asset_Assets {
                  alt
                  width
                  height
                }
              }
            }
          }
        }
      }
    }
  }
`
